import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Fingerprint, Wifi, WifiOff, RefreshCw, Play, Upload, Users,
  CheckCircle, AlertCircle, Loader2, Plus, Trash2, Edit2, X,
  ChevronDown, ChevronUp, Clock, Zap, FileText,
  ToggleLeft, ToggleRight,
} from 'lucide-react';
import { biometricAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Tiny helpers ─────────────────────────────────────────────────────────────
const MODELS = ['Realtime T304F Mini','Realtime T501 Mini','Realtime RS9W','Realtime RS910',
                 'Secureye S-FB3K','ZKTeco K40','ZKTeco F18','Other'];
const MACHINE_TYPES = ['T304fMini','T501MiNi','RS9W','RS910','S-FB3K','F18','K40',''];

function Badge({ label, color = 'slate' }) {
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red:     'bg-red-50 text-red-700 border-red-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    blue:    'bg-blue-50 text-blue-700 border-blue-200',
    slate:   'bg-slate-50 text-slate-600 border-slate-200',
  }[color] || 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>{label}</span>;
}

// ── Device card ───────────────────────────────────────────────────────────────
function DeviceCard({ dev, onTest, onPull, onPullDLL, onEdit, onDelete }) {
  const [testing,  setTesting]  = useState(false);
  const [pulling,  setPulling]  = useState(false);
  const [pullingD, setPullingD] = useState(false);
  const [testRes,  setTestRes]  = useState(null);
  const [pullRes,  setPullRes]  = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  // Date range for sync — default to first day of current month → today
  const todayStr = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [syncFrom, setSyncFrom] = useState(firstOfMonth);
  const [syncTo,   setSyncTo]   = useState(todayStr);

  const handleTest = async () => {
    setTesting(true); setTestRes(null);
    const r = await onTest(dev.id);
    setTestRes(r); setTesting(false);
  };

  const handlePull = async (mode) => {
    if (mode === 'dll') { setPullingD(true); } else { setPulling(true); }
    setPullRes(null);
    const opts = { startDate: syncFrom, endDate: syncTo };
    const r = await (mode === 'dll' ? onPullDLL(dev.id, opts) : onPull(dev.id, opts));
    setPullRes(r);
    if (mode === 'dll') setPullingD(false); else setPulling(false);
  };

  const statusColor = dev.is_active ? 'emerald' : 'slate';
  const lastSync = dev.last_sync_at
    ? new Date(dev.last_sync_at).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })
    : 'Never synced';

  return (
    <div className="glass-card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <Fingerprint size={20} className="text-primary-600"/>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">{dev.name}</h3>
            <p className="text-xs text-slate-500">{dev.model} · {dev.location || 'No location'}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                {dev.ip_address}:{dev.port}
              </code>
              <Badge label={dev.machine_type || 'default'} color="blue"/>
              <Badge label={dev.is_active ? 'Active' : 'Inactive'} color={statusColor}/>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(dev)}
            className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors" title="Edit">
            <Edit2 size={14}/>
          </button>
          <button onClick={() => onDelete(dev.id)}
            className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors" title="Delete">
            <Trash2 size={14}/>
          </button>
        </div>
      </div>

      {/* Last sync info */}
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Clock size={11}/>
        <span>Last sync: {lastSync}</span>
        {dev.last_sync_count > 0 && <span>· {dev.last_sync_count} records</span>}
      </div>

      {/* Sync date range */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-400 font-medium whitespace-nowrap">Sync range:</span>
        <input
          type="date" value={syncFrom} onChange={e => setSyncFrom(e.target.value)}
          className="border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <span className="text-[11px] text-slate-400">to</span>
        <input
          type="date" value={syncTo} onChange={e => setSyncTo(e.target.value)}
          className="border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button onClick={handleTest} disabled={testing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-60 transition-colors">
          {testing ? <Loader2 size={12} className="animate-spin"/> : <Wifi size={12}/>}
          Test Connection
        </button>
        <button onClick={() => handlePull('zk')} disabled={pulling}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg disabled:opacity-60 transition-colors">
          {pulling ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
          {pulling ? 'Pulling…' : 'Pull (ZKTeco SDK)'}
        </button>
        <button onClick={() => handlePull('dll')} disabled={pullingD}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 rounded-lg disabled:opacity-60 transition-colors">
          {pullingD ? <Loader2 size={12} className="animate-spin text-white"/> : <Zap size={12}/>}
          {pullingD ? 'Pulling…' : 'Pull (DLL / TCP)'}
        </button>
      </div>

      {/* Test result */}
      {testRes && (() => {
        const isEc5 = !testRes.sdkAvailable && (
          testRes.dllErrorCode === 5 ||
          testRes.dllError?.includes('ec=5') || testRes.dllError?.includes('ERR_NON_CARRYOUT') ||
          testRes.message?.includes('ec=5')  || testRes.message?.includes('ERR_NON_CARRYOUT')
        );
        const dllWorking = !testRes.sdkAvailable && testRes.hasRecentSync;
        return (
          <div className={`rounded-xl p-3 text-xs border space-y-2 ${
            testRes.sdkAvailable
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : dllWorking
                ? 'bg-blue-50 border-blue-200 text-blue-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            {/* Status line */}
            <p className="font-semibold flex items-center gap-1.5">
              {testRes.sdkAvailable
                ? <><CheckCircle size={12}/> SDK Connected</>
                : dllWorking
                  ? <><CheckCircle size={12} className="text-blue-600"/> DLL Pull working · test password mismatch</>
                  : <><WifiOff size={12}/> Port reachable — SDK handshake failed</>
              }
              {testRes.sdkType && <Badge label={testRes.sdkType} color="blue"/>}
            </p>

            {/* DLL working but test password wrong — show helpful info box */}
            {dllWorking && (
              <div className="bg-blue-100 border border-blue-200 rounded-lg px-3 py-2 space-y-1 text-blue-800">
                <p className="font-semibold">✅ DLL / TCP pull is working fine</p>
                <p>
                  Last sync: <strong>{testRes.lastSyncCount} records</strong> on{' '}
                  {new Date(testRes.lastSyncAt).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}.
                </p>
                <p>
                  Test uses only the saved password — it didn't match. <strong>Pull (DLL/TCP)</strong> tries
                  all common passwords automatically. Click it to sync; the working password will be saved.
                </p>
              </div>
            )}

            {/* Generic / EC=5 message (only when NOT the dllWorking case) */}
            {!dllWorking && !isEc5 && testRes.message && <p className="text-amber-700">{testRes.message}</p>}
            {testRes.firmwareVersion && <p>Firmware: {testRes.firmwareVersion} · SN: {testRes.serialNumber}</p>}
            {testRes.logCount > 0 && <p>Records on device: {testRes.logCount}</p>}
            {!dllWorking && testRes.lastAdmsPush && (
              <p>Last sync: {new Date(testRes.lastAdmsPush).toLocaleString('en-IN')}</p>
            )}

            {/* EC=5 targeted troubleshooting */}
            {isEc5 && (
              <div className="bg-white/70 border border-amber-200 rounded-lg p-3 space-y-2.5">
                <p className="font-bold text-amber-900 flex items-center gap-1.5">
                  <AlertCircle size={13}/> EC=5 — IP SDK is disabled or wrong password
                </p>
                <p className="text-amber-800">
                  Port {dev.port} is reachable but the device rejected the SDK handshake.
                  Test tried only the configured password:{' '}
                  <code className="bg-amber-100 px-1 rounded font-mono">
                    {testRes.triedPasswords || String(dev.comm_password ?? 0)}
                  </code>
                  {' '}— failed.
                </p>
                <div className="bg-amber-100 rounded-lg px-3 py-2 text-amber-800 text-[11px]">
                  <strong>Try first:</strong> Click <strong>"Pull (DLL/TCP)"</strong> — it will automatically
                  attempt all common passwords:{' '}
                  <code className="font-mono">0 · 2703 · 1234 · 4321 · 1111 · 9999 · 8888</code>.
                  If any matches your device, it will sync.
                </div>

                <div className="space-y-1.5">
                  <p className="font-semibold text-amber-900">Fix 1 — Enable IP SDK on the device (most likely cause):</p>
                  {[
                    'Press Menu on the T304F Mini',
                    'Go to Comm Settings (or Settings → Communication)',
                    'Find "IP SDK" or "Cloud / Advanced" option',
                    'Set IP SDK → Enable: ON',
                    'Set Comm Password (or Device Password): 0  (blank/zero)',
                    'Save → restart device → click Test Connection again',
                  ].map((s, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <span className="w-4 h-4 rounded-full bg-amber-200 text-amber-900 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <p className="font-semibold text-amber-900">Fix 2 — Wrong device IP (IP may have changed via DHCP):</p>
                  {[
                    `Current IP in settings: ${dev.ip_address} — verify this matches the device screen`,
                    'On device: Menu → System Info (or Network) → shows current IP address',
                    'If IP differs, click the Edit (pencil) button on this card and update it',
                  ].map((s, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <span className="w-4 h-4 rounded-full bg-amber-200 text-amber-900 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>
        );
      })()}

      {/* Pull result */}
      {pullRes && (
        <div className={`rounded-xl p-3 text-xs border ${
          pullRes.error
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-emerald-50 border-emerald-200 text-emerald-800'
        }`}>
          {pullRes.error ? (
            <p className="font-medium flex items-center gap-1.5">
              <AlertCircle size={12}/> {pullRes.error}
            </p>
          ) : (
            <>
              <p className="font-semibold flex items-center gap-1.5 mb-1">
                <CheckCircle size={12}/> {pullRes.message}
              </p>
              <div className="flex gap-3">
                <span>✅ Synced: <strong>{pullRes.synced}</strong></span>
                {pullRes.unmatched > 0 && <span>⚠ Unmatched: <strong>{pullRes.unmatched}</strong></span>}
                {pullRes.totalRawPunches && <span>Raw punches: {pullRes.totalRawPunches}</span>}
              </div>
              {pullRes.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Errors ({pullRes.errors.length})</summary>
                  <div className="mt-0.5 space-y-0.5">{pullRes.errors.map((e,i) => <div key={i}>{e}</div>)}</div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Device form modal ─────────────────────────────────────────────────────────
function DeviceForm({ device, onSave, onClose }) {
  const [form, setForm] = useState({
    name:         device?.name         || 'Main Biometric Device',
    model:        device?.model        || 'Realtime T304F Mini',
    location:     device?.location     || 'Main Entrance',
    ipAddress:    device?.ip_address   || '10.10.74.94',
    port:         device?.port         || 5005,
    commPassword: device?.comm_password ?? 0,   // ?? not || so 0 is kept as 0
    machineType:  device?.machine_type || 'T304fMini',
    machineNo:    device?.machine_no   ?? 1,
    isActive:     device?.is_active    !== undefined ? !!device.is_active : true,
  });
  const [saving, setSaving] = useState(false);

  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    await onSave({ ...form });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-800">
            {device ? 'Edit Device' : 'Add Biometric Device'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X size={16}/>
          </button>
        </div>

        {[
          { label: 'Device Name',                                key: 'name',         type: 'text'   },
          { label: 'IP Address',                                 key: 'ipAddress',    type: 'text'   },
          { label: 'Port',                                       key: 'port',         type: 'number' },
          { label: 'Comm Password  (0 = no password)',           key: 'commPassword', type: 'number' },
          { label: 'Machine No  (Device ID, usually 1)',         key: 'machineNo',    type: 'number' },
          { label: 'Location',                                   key: 'location',     type: 'text'   },
        ].map(({ label, key, type }) => (
          <div key={key}>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
            <input type={type} value={form[key]}
              onChange={e => handle(key, type === 'number' ? parseInt(e.target.value)||0 : e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"/>
          </div>
        ))}

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Model</label>
          <select value={form.model} onChange={e => handle('model', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
            {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
            Machine Type <span className="normal-case font-normal">(for SBXPCDLL)</span>
          </label>
          <select value={form.machineType} onChange={e => handle('machineType', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
            {MACHINE_TYPES.map(t => <option key={t} value={t}>{t || '(default)'}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.isActive} onChange={e => handle('isActive', e.target.checked)}
            className="w-4 h-4 accent-primary-600"/>
          <span className="text-sm text-slate-700">Active</span>
        </label>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2 text-sm font-semibold bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-60 transition-colors">
            {saving ? 'Saving…' : device ? 'Update' : 'Add Device'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════════════════
export default function BiometricSync({ embedded = false }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const canManage = isAdmin || isSuperAdmin;

  const [devices,     setDevices]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editDev,     setEditDev]     = useState(null);

  const [empMapping,  setEmpMapping]  = useState([]);
  const [showMapping, setShowMapping] = useState(false);
  const [mapLoading,  setMapLoading]  = useState(false);
  const [mapSaving,   setMapSaving]   = useState({});

  const [importFile,  setImportFile]  = useState(null);
  const [importing,   setImporting]   = useState(false);
  const [importResult,setImportResult]= useState(null);

  const [syncingAll,    setSyncingAll]    = useState(false);
  const [syncAllResult, setSyncAllResult] = useState(null);

  // Auto-sync state
  const [autoSync,          setAutoSync]          = useState({
    enabled: false, interval: 30, active: false, running: false,
    lastRunAt: null, nextRunAt: null, lastResult: null,
  });
  const [autoSyncSaving,    setAutoSyncSaving]    = useState(false);
  const [autoSyncTrigering, setAutoSyncTrigering] = useState(false);
  const autoSyncPollRef = useRef(null);

  const [toast, setToast] = useState(null);
  const fileRef = useRef();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load devices ────────────────────────────────────────────────────────────
  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await biometricAPI.getDevices();
      setDevices(r.data || []);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to load devices', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // ── Load auto-sync status (poll every 30s) ───────────────────────────────────
  const loadAutoSyncStatus = useCallback(async () => {
    try {
      const r = await biometricAPI.getAutoSyncStatus();
      setAutoSync(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadAutoSyncStatus();
    autoSyncPollRef.current = setInterval(loadAutoSyncStatus, 30000);
    return () => clearInterval(autoSyncPollRef.current);
  }, [loadAutoSyncStatus]);

  // ── Device CRUD ─────────────────────────────────────────────────────────────
  const saveDevice = async (data) => {
    try {
      if (editDev) {
        await biometricAPI.updateDevice(editDev.id, data);
        showToast('Device updated');
      } else {
        await biometricAPI.addDevice(data);
        showToast('Device added');
      }
      setShowForm(false); setEditDev(null);
      loadDevices();
    } catch (e) { showToast(e.response?.data?.error || 'Save failed', 'error'); }
  };

  const deleteDevice = async (id) => {
    if (!window.confirm('Delete this device?')) return;
    try {
      await biometricAPI.deleteDevice(id);
      showToast('Device deleted');
      loadDevices();
    } catch (e) { showToast(e.response?.data?.error || 'Delete failed', 'error'); }
  };

  // ── Device actions ──────────────────────────────────────────────────────────
  const testConnection = async (id) => {
    try {
      const r = await biometricAPI.testConnection(id);
      return r.data;
    } catch (e) {
      return { connected: false, error: e.response?.data?.error || e.message };
    }
  };

  const pullAttendance = async (id, opts) => {
    try {
      const r = await biometricAPI.pullAttendance(id, opts);
      loadDevices();
      return r.data;
    } catch (e) {
      return { error: e.response?.data?.error || e.message };
    }
  };

  const pullViaDLL = async (id, opts) => {
    try {
      const r = await biometricAPI.pullViaDLL(id, opts);
      loadDevices();
      return r.data;
    } catch (e) {
      return { error: e.response?.data?.error || e.message };
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true); setSyncAllResult(null);
    try {
      const r = await biometricAPI.syncAll();
      setSyncAllResult(r.data);
      loadDevices();
      showToast(r.data.message || 'Sync all complete');
    } catch (e) {
      const err = e.response?.data?.error || e.message;
      setSyncAllResult({ error: err });
      showToast(err, 'error');
    } finally { setSyncingAll(false); }
  };

  // ── Auto-sync config ────────────────────────────────────────────────────────
  const saveAutoSyncConfig = async (enabled, interval) => {
    setAutoSyncSaving(true);
    try {
      const r = await biometricAPI.saveAutoSyncConfig({ enabled, interval });
      setAutoSync(a => ({ ...a, enabled: r.data.enabled, interval: r.data.interval }));
      showToast(`Auto-sync ${enabled ? 'enabled' : 'disabled'}`);
      // Refresh full state (timer may have restarted)
      setTimeout(loadAutoSyncStatus, 800);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to save', 'error'); }
    finally { setAutoSyncSaving(false); }
  };

  const triggerAutoSyncNow = async () => {
    setAutoSyncTrigering(true);
    try {
      await biometricAPI.triggerAutoSync();
      showToast('Auto-sync started');
      setTimeout(loadAutoSyncStatus, 2000);
    } catch (e) { showToast(e.response?.data?.error || 'Trigger failed', 'error'); }
    finally { setAutoSyncTrigering(false); }
  };

  // ── Employee mapping ────────────────────────────────────────────────────────
  const loadMapping = async () => {
    setMapLoading(true);
    try {
      const r = await biometricAPI.getEmployeeMapping();
      setEmpMapping(r.data || []);
      setShowMapping(true);
    } catch (e) { showToast('Failed to load mapping', 'error'); }
    finally { setMapLoading(false); }
  };

  const saveMapping = async (empId, uid) => {
    setMapSaving(s => ({ ...s, [empId]: true }));
    try {
      await biometricAPI.updateBiometricUid(empId, { biometricUid: uid });
      setEmpMapping(m => m.map(e => e.id === empId ? { ...e, biometric_uid: uid } : e));
    } catch (e) { showToast('Failed to save UID', 'error'); }
    finally { setMapSaving(s => ({ ...s, [empId]: false })); }
  };

  // ── File import ─────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await biometricAPI.importDeviceFile(fd);
      setImportResult(r.data);
      showToast(r.data.message);
    } catch (e) {
      const err = e.response?.data?.error || e.message;
      setImportResult({ error: err });
      showToast(err, 'error');
    } finally { setImporting(false); }
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-5xl mx-auto space-y-6'}>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2
          ${toast.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
          {toast.type === 'error' ? <AlertCircle size={16}/> : <CheckCircle size={16}/>}
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Fingerprint size={22} className="text-primary-600"/> Biometric Device Sync
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage and sync all biometric attendance devices
            {devices.length > 0 && (
              <span className="ml-1.5 text-primary-600 font-medium">
                · {devices.length} device{devices.length !== 1 ? 's' : ''} configured
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && devices.length >= 2 && (
            <button onClick={handleSyncAll} disabled={syncingAll}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-primary-300 text-primary-700 bg-primary-50 rounded-xl hover:bg-primary-100 disabled:opacity-60 transition-colors">
              {syncingAll ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
              {syncingAll ? 'Syncing All…' : 'Sync All Devices'}
            </button>
          )}
          {canManage && (
            <button onClick={() => { setEditDev(null); setShowForm(true); }}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors">
              <Plus size={14}/> Add Device
            </button>
          )}
        </div>
      </div>

      {/* Device list */}
      <div className="space-y-4">
        {loading ? (
          <div className="glass-card p-10 flex justify-center">
            <Loader2 size={24} className="animate-spin text-primary-500"/>
          </div>
        ) : devices.length === 0 ? (
          <div className="glass-card p-10 text-center">
            <Fingerprint size={32} className="text-slate-300 mx-auto mb-3"/>
            <p className="text-slate-500 text-sm">No biometric devices configured.</p>
            {canManage && (
              <button onClick={() => setShowForm(true)}
                className="mt-3 text-primary-600 text-sm font-medium hover:underline">
                Add your first device
              </button>
            )}
          </div>
        ) : (
          devices.map(dev => (
            <DeviceCard
              key={dev.id} dev={dev}
              onTest={testConnection}
              onPull={pullAttendance}
              onPullDLL={pullViaDLL}
              onEdit={d => { setEditDev(d); setShowForm(true); }}
              onDelete={deleteDevice}
            />
          ))
        )}
      </div>

      {/* ── Sync All Results ────────────────────────────────────────────────── */}
      {syncAllResult && (
        <div className={`glass-card p-4 space-y-3 border ${
          syncAllResult.error ? 'border-red-200'
            : syncAllResult.totalUnmatched > 0 && !syncAllResult.totalSynced
              ? 'border-amber-200'
              : 'border-emerald-200'
        }`}>
          {syncAllResult.error ? (
            <p className="text-red-700 text-sm font-medium flex items-center gap-2">
              <AlertCircle size={14}/> {syncAllResult.error}
            </p>
          ) : (
            <>
              {/* Header summary */}
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <CheckCircle size={14} className="text-emerald-600"/> {syncAllResult.message}
                </p>
                {(syncAllResult.totalRawPunches ?? 0) > 0 && (
                  <span className="text-xs text-slate-400">· {syncAllResult.totalRawPunches} raw punches</span>
                )}
                {(syncAllResult.totalUnmatched ?? 0) > 0 && (
                  <span className="text-xs text-amber-600 font-medium">· ⚠ {syncAllResult.totalUnmatched} unmatched</span>
                )}
              </div>

              {/* Unmatched warning */}
              {(syncAllResult.totalUnmatched ?? 0) > 0 && !syncAllResult.totalSynced && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800">
                  <p className="font-semibold mb-0.5 flex items-center gap-1.5">
                    <AlertCircle size={12}/> Punches received but no employee matches found
                  </p>
                  <p>Set each employee's enrollment number in the <strong>Employee Biometric UID Mapping</strong> section below.</p>
                </div>
              )}

              {/* Per-device rows */}
              <div className="space-y-1.5">
                {syncAllResult.results?.map(r => (
                  <div key={r.id} className={`flex items-center gap-3 text-xs px-3 py-2 rounded-xl border flex-wrap ${
                    r.error
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : r.unmatched > 0 && !r.synced
                        ? 'bg-amber-50 border-amber-100 text-amber-800'
                        : 'bg-slate-50 border-slate-100 text-slate-700'
                  }`}>
                    <span className="font-semibold min-w-[130px]">{r.name}</span>
                    <code className="text-slate-400 font-mono text-[10px]">{r.ip}</code>
                    {r.error ? (
                      <span className="ml-auto text-red-600 truncate max-w-[300px]" title={r.error}>{r.error}</span>
                    ) : (
                      <span className="ml-auto flex items-center gap-2 flex-wrap">
                        {r.synced > 0 && <span className="text-emerald-700 font-medium">✅ {r.synced} synced</span>}
                        {(r.rawPunches ?? 0) > 0 && <span className="text-slate-400">{r.rawPunches} raw</span>}
                        {r.unmatched > 0 && <span className="text-amber-600">⚠ {r.unmatched} unmatched</span>}
                        {!r.synced && !r.unmatched && !(r.rawPunches) && (
                          <span className="text-slate-400">0 records</span>
                        )}
                        <Badge label={r.method || 'DLL'} color="blue"/>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Auto-Sync Settings ───────────────────────────────────────────────── */}
      {canManage && (
        <div className="glass-card p-5 space-y-4">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Clock size={16} className="text-primary-600"/> Auto-Sync Schedule
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Automatically pull attendance from all active devices on a schedule
              </p>
            </div>
            {/* Enable / disable toggle */}
            <button
              onClick={() => saveAutoSyncConfig(!autoSync.enabled, autoSync.interval)}
              disabled={autoSyncSaving}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                autoSync.enabled
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
              }`}>
              {autoSyncSaving
                ? <Loader2 size={16} className="animate-spin"/>
                : autoSync.enabled ? <ToggleRight size={18}/> : <ToggleLeft size={18}/>}
              {autoSync.enabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Interval selector */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Sync Interval
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { min: 15, label: '15 min' },
                  { min: 30, label: '30 min' },
                  { min: 60, label: '1 hr'   },
                  { min: 120, label: '2 hr'  },
                  { min: 240, label: '4 hr'  },
                ].map(({ min, label }) => (
                  <button key={min}
                    onClick={() => saveAutoSyncConfig(autoSync.enabled, min)}
                    disabled={autoSyncSaving}
                    className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                      autoSync.interval === min
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Live status */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                Status
              </label>
              <div className="flex items-center gap-2 text-xs">
                {autoSync.running ? (
                  <span className="flex items-center gap-1.5 text-blue-700">
                    <Loader2 size={12} className="animate-spin"/> Syncing now…
                  </span>
                ) : autoSync.active ? (
                  <span className="flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle size={12}/> Scheduled · every {autoSync.interval} min
                  </span>
                ) : (
                  <span className="text-slate-400">Not scheduled (disabled)</span>
                )}
              </div>
              {autoSync.lastRunAt && (
                <p className="text-[11px] text-slate-400">
                  Last run: {new Date(autoSync.lastRunAt).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}
                </p>
              )}
              {autoSync.nextRunAt && autoSync.active && (
                <p className="text-[11px] text-slate-400">
                  Next run: {new Date(autoSync.nextRunAt).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}
                </p>
              )}
            </div>
          </div>

          {/* Last result breakdown */}
          {autoSync.lastResult && (
            <div className={`rounded-xl px-3 py-2.5 text-xs border space-y-2 ${
              autoSync.lastResult.error
                ? 'bg-red-50 border-red-200'
                : autoSync.lastResult.totalUnmatched > 0 && autoSync.lastResult.totalSynced === 0
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-slate-50 border-slate-100'
            }`}>
              {autoSync.lastResult.error ? (
                <span className="flex items-center gap-1.5 text-red-700">
                  <AlertCircle size={11}/> Last sync error: {autoSync.lastResult.error}
                </span>
              ) : (
                <>
                  {/* Summary row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`font-semibold ${autoSync.lastResult.totalSynced > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                      ✅ {autoSync.lastResult.totalSynced ?? 0} synced
                    </span>
                    {(autoSync.lastResult.totalRawPunches ?? 0) > 0 && (
                      <span className="text-slate-400">
                        · {autoSync.lastResult.totalRawPunches} raw punches
                      </span>
                    )}
                    {(autoSync.lastResult.totalUnmatched ?? 0) > 0 && (
                      <span className="text-amber-600 font-medium">
                        ⚠ {autoSync.lastResult.totalUnmatched} unmatched
                      </span>
                    )}
                    <span className="text-slate-400 ml-auto">
                      {autoSync.lastResult.devices ?? 0} device{(autoSync.lastResult.devices ?? 0) !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Unmatched warning */}
                  {(autoSync.lastResult.totalUnmatched ?? 0) > 0 && autoSync.lastResult.totalSynced === 0 && (
                    <div className="bg-amber-100 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 space-y-1">
                      <p className="font-semibold flex items-center gap-1.5">
                        <AlertCircle size={11}/> Device records not matched to employees
                      </p>
                      <p>
                        The device returned <strong>{autoSync.lastResult.totalRawPunches ?? autoSync.lastResult.totalUnmatched}</strong> punches
                        but none matched any employee. Fix: go to{' '}
                        <strong>Employee Biometric UID Mapping</strong> below and enter each
                        employee's enrollment number (the number on their biometric device profile).
                      </p>
                    </div>
                  )}

                  {/* Per-device rows (show only if there's a mix of errors/unmatched) */}
                  {autoSync.lastResult.results?.some(r => r.error || r.unmatched > 0) && (
                    <div className="space-y-1 pt-1 border-t border-slate-200">
                      {autoSync.lastResult.results.map(r => (
                        <div key={r.id} className={`flex items-center gap-2 text-[11px] flex-wrap ${
                          r.error ? 'text-red-600' : r.unmatched > 0 ? 'text-amber-700' : 'text-slate-500'
                        }`}>
                          <span className="font-medium min-w-[110px]">{r.name}</span>
                          <span className="text-slate-400 font-mono">{r.ip}</span>
                          {r.error ? (
                            <span className="ml-auto truncate max-w-[220px]" title={r.error}>❌ {r.error}</span>
                          ) : (
                            <span className="ml-auto">
                              {r.synced > 0 && `✅ ${r.synced} synced`}
                              {r.unmatched > 0 && ` · ⚠ ${r.unmatched} unmatched`}
                              {r.rawPunches > 0 && ` (${r.rawPunches} raw)`}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Manual trigger */}
          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <button
              onClick={triggerAutoSyncNow}
              disabled={autoSyncTrigering || autoSync.running}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-60 transition-colors">
              {autoSyncTrigering || autoSync.running
                ? <Loader2 size={12} className="animate-spin"/>
                : <RefreshCw size={12}/>}
              {autoSyncTrigering || autoSync.running ? 'Syncing…' : 'Sync Now'}
            </button>
            <span className="text-[11px] text-slate-400">
              Manually trigger a sync of all active devices immediately
            </span>
          </div>
        </div>
      )}

      {/* File import */}
      <div className="glass-card p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <FileText size={16} className="text-primary-600"/> Import from Device File
        </h2>
        <p className="text-xs text-slate-500">
          Export attendance from the device to a USB drive, then upload the file here.
          Supports Realtime report (.txt, .csv) and Excel (.xlsx) exports.
        </p>
        <div className="flex gap-3 items-center flex-wrap">
          <input ref={fileRef} type="file"
            accept=".txt,.csv,.log,.xlsx,.xls,.dat"
            onChange={e => setImportFile(e.target.files[0])}
            className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-slate-200 file:text-xs file:font-medium file:bg-white hover:file:bg-slate-50"/>
          <button onClick={handleImport} disabled={!importFile || importing}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors">
            {importing ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {importing ? 'Importing…' : 'Import File'}
          </button>
        </div>
        {importResult && (
          <div className={`rounded-xl p-3 text-xs border ${
            importResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}>
            {importResult.error ? (
              <p className="font-medium flex items-center gap-1.5"><AlertCircle size={12}/> {importResult.error}</p>
            ) : (
              <>
                <p className="font-semibold">{importResult.message}</p>
                <p className="mt-0.5">Synced: {importResult.synced} · Unmatched: {importResult.unmatched}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Employee UID mapping */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <Users size={16} className="text-primary-600"/> Employee Biometric UID Mapping
          </h2>
          <button onClick={showMapping ? () => setShowMapping(false) : loadMapping}
            disabled={mapLoading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-60 transition-colors">
            {mapLoading ? <Loader2 size={12} className="animate-spin"/> : (showMapping ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
            {showMapping ? 'Hide' : 'View / Edit Mapping'}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Map each employee to their biometric enrollment number on the device.
          The sync uses this mapping to link attendance punches to the correct employee.
        </p>

        {showMapping && empMapping.length > 0 && (
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 grid grid-cols-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              <span>Employee</span>
              <span>Employee Code</span>
              <span>Biometric UID</span>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
              {empMapping.map(emp => (
                <div key={emp.id} className="px-4 py-2 grid grid-cols-3 items-center gap-2 text-xs">
                  <span className="text-slate-700 font-medium truncate">{emp.name}</span>
                  <span className="font-mono text-slate-400 text-[11px]">{emp.employee_code || '—'}</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      defaultValue={emp.biometric_uid || ''}
                      onBlur={e => {
                        const v = e.target.value.trim();
                        if (v !== (emp.biometric_uid || '')) saveMapping(emp.id, v || null);
                      }}
                      className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1 font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                      placeholder="UID…"
                    />
                    {mapSaving[emp.id] && <Loader2 size={11} className="animate-spin text-primary-500 flex-shrink-0"/>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Device form modal */}
      {showForm && (
        <DeviceForm
          device={editDev}
          onSave={saveDevice}
          onClose={() => { setShowForm(false); setEditDev(null); }}
        />
      )}
    </div>
  );
}
